import {
  loadReadIds,
  notifyCareCommunicationRead,
  rememberReadIds,
  type InboundLetter,
  type ReadStorage,
} from '@/lib/medcom/careCommunicationRead';

export interface ClinicReadStore {
  list(clinicId: string): Promise<string[] | null>;
  save(clinicId: string, messageKey: string): Promise<boolean>;
}

const published = new Set<string>();

function publishToken(clinicId: string, messageKey: string): string {
  return `${clinicId}\0${messageKey}`;
}

/** Test isolation. */
export function resetPublishedReads(): void {
  published.clear();
}

/**
 * When one copy of a letter is already open, every other row with the same
 * communication id is the same letter. Returns the keys that still need to be
 * stored. A letter with no opened copy is left out.
 */
export function siblingReadKeys(
  letters: readonly InboundLetter[],
  readIds: ReadonlySet<string>,
): string[] {
  const groups = new Map<string, InboundLetter[]>();
  for (const letter of letters) {
    if (letter.direction !== 'in') continue;
    const comm = letter.communicationIdentifier;
    if (!comm) continue;
    const group = groups.get(comm) ?? [];
    group.push(letter);
    groups.set(comm, group);
  }
  const extra: string[] = [];
  for (const [comm, group] of groups) {
    const opened = readIds.has(comm) || group.some((letter) => readIds.has(letter.id));
    if (!opened) continue;
    if (!readIds.has(comm)) extra.push(comm);
    for (const letter of group) {
      if (!readIds.has(letter.id)) extra.push(letter.id);
    }
  }
  return extra;
}

export function planReadSync(
  localIds: Iterable<string>,
  serverIds: Iterable<string>,
): { merged: string[]; upload: string[] } {
  const local = [...localIds].filter((id) => id.length > 0);
  const server = [...serverIds].filter((id) => id.length > 0);
  const onServer = new Set(server);
  const upload = local.filter((id) => !onServer.has(id));
  return { merged: [...new Set([...local, ...server])], upload };
}

export async function publishReadKey(
  clinicId: string,
  messageKey: string,
  store: ClinicReadStore,
): Promise<void> {
  if (!clinicId || !messageKey) return;
  const token = publishToken(clinicId, messageKey);
  if (published.has(token)) return;
  published.add(token);
  const saved = await store.save(clinicId, messageKey);
  if (!saved) published.delete(token);
}

export async function pullClinicReads(
  clinicId: string,
  storage: ReadStorage,
  store: ClinicReadStore,
): Promise<string[] | null> {
  const server = await store.list(clinicId);
  if (!server) return null;
  const plan = planReadSync(loadReadIds(storage, clinicId), server);
  if (rememberReadIds(storage, clinicId, plan.merged)) notifyCareCommunicationRead();
  for (const key of server) published.add(publishToken(clinicId, key));
  for (const key of plan.upload) void publishReadKey(clinicId, key, store);
  return plan.merged;
}
